/**
 * Tipos y configuración para el sistema de grupos segmentado
 * Logrit Familia vs Logrit Educa
 */

// ============================================
// TIPOS BASE
// ============================================

/**
 * Tipo de licencia del usuario
 */
export type LicenseType = 'familia' | 'educacion';

/**
 * Tipos de grupo disponibles según la licencia
 */
export type GroupType = 
  // Familia
  | 'familia' 
  | 'pareja' 
  | 'amigos'
  // Educación
  | 'grado'
  | 'curso'
  | 'grupo';

// ============================================
// CONFIGURACIÓN DE TIPOS DE GRUPO
// ============================================

export interface GroupTypeConfig {
  id: GroupType;
  label: string;
  emoji: string;
  description: string;
  licenseType: LicenseType;
}

/**
 * Configuración completa de todos los tipos de grupo
 */
export const GROUP_TYPES_CONFIG: Record<GroupType, GroupTypeConfig> = {
  // LOGRIT FAMILIA
  familia: {
    id: 'familia',
    label: 'Familia',
    emoji: '👨‍👩‍👧‍👦',
    description: 'Para organizar tareas y actividades familiares',
    licenseType: 'familia'
  },
  pareja: {
    id: 'pareja',
    label: 'Pareja',
    emoji: '💑',
    description: 'Para gestionar actividades en pareja',
    licenseType: 'familia'
  },
  amigos: {
    id: 'amigos',
    label: 'Amigos',
    emoji: '👥',
    description: 'Para organizar actividades con amigos',
    licenseType: 'familia'
  },
  
  // LOGRIT EDUCA
  grado: {
    id: 'grado',
    label: 'Grado',
    emoji: '🎓',
    description: 'Para gestionar un grado escolar completo',
    licenseType: 'educacion'
  },
  curso: {
    id: 'curso',
    label: 'Curso',
    emoji: '📚',
    description: 'Para organizar un curso o materia específica',
    licenseType: 'educacion'
  },
  grupo: {
    id: 'grupo',
    label: 'Grupo',
    emoji: '🎯',
    description: 'Para gestionar grupos de trabajo o estudio',
    licenseType: 'educacion'
  }
};

// ============================================
// HELPERS
// ============================================

/**
 * Obtiene los tipos de grupo disponibles según la licencia
 */
export function getGroupTypesForLicense(licenseType: LicenseType): GroupTypeConfig[] {
  return Object.values(GROUP_TYPES_CONFIG).filter(
    config => config.licenseType === licenseType
  );
}

/**
 * Obtiene la configuración de un tipo de grupo específico
 */
export function getGroupTypeConfig(groupType: GroupType): GroupTypeConfig {
  return GROUP_TYPES_CONFIG[groupType];
}

/**
 * Verifica si un tipo de grupo es válido para una licencia
 */
export function isValidGroupTypeForLicense(
  groupType: GroupType, 
  licenseType: LicenseType
): boolean {
  const config = GROUP_TYPES_CONFIG[groupType];
  return config?.licenseType === licenseType;
}

/**
 * Obtiene el tipo de grupo por defecto según la licencia
 */
export function getDefaultGroupType(licenseType: LicenseType): GroupType {
  return licenseType === 'familia' ? 'familia' : 'grado';
}

// ============================================
// PERSONALIZACIÓN DE COPYS POR CONTEXTO
// ============================================

export interface ContextCopys {
  // Títulos generales con emojis
  dashboard: string;
  dashboardEmoji: string;
  createNew: string;
  
  // Miembros con emojis
  member: string;
  memberEmoji: string;
  members: string;
  membersEmoji: string;
  addMember: string;
  
  // Actividades con emojis
  activity: string;
  activityEmoji: string;
  activities: string;
  activitiesEmoji: string;
  
  // Tareas con emojis
  task: string;
  taskEmoji: string;
  tasks: string;
  tasksEmoji: string;
  
  // Puntos/Unidades con emojis
  points: string;
  pointsEmoji: string;
  
  // Acciones con emojis
  complete: string;
  completeEmoji: string;
  assign: string;
  assignEmoji: string;
  
  // Adicionales
  viewAll: string;
  addTask: string;
  addActivity: string;
  myProgress: string;
  groupProgress: string;
  
  // Rankings y estadísticas
  ranking: string;
  rankingEmoji: string;
  todayRanking: string;
  totalRanking: string;
  
  // Roles
  adminRole: string;
  memberRole: string;
}

/**
 * Copys personalizados según el tipo de grupo
 */
export const CONTEXT_COPYS: Record<GroupType, ContextCopys> = {
  // FAMILIA
  familia: {
    dashboard: 'contextCopys.familia.dashboard',
    dashboardEmoji: '🏠',
    createNew: 'contextCopys.familia.createNew',
    member: 'contextCopys.familia.member',
    memberEmoji: '👤',
    members: 'contextCopys.familia.members',
    membersEmoji: '👥',
    addMember: 'contextCopys.familia.addMember',
    activity: 'contextCopys.familia.activity',
    activityEmoji: '⚡',
    activities: 'contextCopys.familia.activities',
    activitiesEmoji: '⚡',
    task: 'contextCopys.familia.task',
    taskEmoji: '✅',
    tasks: 'contextCopys.familia.tasks',
    tasksEmoji: '✅',
    points: 'contextCopys.familia.points',
    pointsEmoji: '⭐',
    complete: 'contextCopys.familia.complete',
    completeEmoji: '✓',
    assign: 'contextCopys.familia.assign',
    assignEmoji: '📌',
    viewAll: 'contextCopys.familia.viewAll',
    addTask: 'contextCopys.familia.addTask',
    addActivity: 'contextCopys.familia.addActivity',
    myProgress: 'contextCopys.familia.myProgress',
    groupProgress: 'contextCopys.familia.groupProgress',
    ranking: 'contextCopys.familia.ranking',
    rankingEmoji: '📊',
    todayRanking: 'contextCopys.familia.todayRanking',
    totalRanking: 'contextCopys.familia.totalRanking',
    adminRole: 'contextCopys.familia.adminRole',
    memberRole: 'contextCopys.familia.memberRole'
  },
  
  pareja: {
    dashboard: 'contextCopys.pareja.dashboard',
    dashboardEmoji: '💑',
    createNew: 'contextCopys.pareja.createNew',
    member: 'contextCopys.pareja.member',
    memberEmoji: '💝',
    members: 'contextCopys.pareja.members',
    membersEmoji: '💑',
    addMember: 'contextCopys.pareja.addMember',
    activity: 'contextCopys.pareja.activity',
    activityEmoji: '💪',
    activities: 'contextCopys.pareja.activities',
    activitiesEmoji: '💪',
    task: 'contextCopys.pareja.task',
    taskEmoji: '💕',
    tasks: 'contextCopys.pareja.tasks',
    tasksEmoji: '💕',
    points: 'contextCopys.pareja.points',
    pointsEmoji: '⭐',
    complete: 'contextCopys.pareja.complete',
    completeEmoji: '✓',
    assign: 'contextCopys.pareja.assign',
    assignEmoji: '📌',
    viewAll: 'contextCopys.pareja.viewAll',
    addTask: 'contextCopys.pareja.addTask',
    addActivity: 'contextCopys.pareja.addActivity',
    myProgress: 'contextCopys.pareja.myProgress',
    groupProgress: 'contextCopys.pareja.groupProgress',
    ranking: 'contextCopys.pareja.ranking',
    rankingEmoji: '💑',
    todayRanking: 'contextCopys.pareja.todayRanking',
    totalRanking: 'contextCopys.pareja.totalRanking',
    adminRole: 'contextCopys.pareja.adminRole',
    memberRole: 'contextCopys.pareja.memberRole'
  },
  
  amigos: {
    dashboard: 'contextCopys.amigos.dashboard',
    dashboardEmoji: '🎯',
    createNew: 'contextCopys.amigos.createNew',
    member: 'contextCopys.amigos.member',
    memberEmoji: '🙋',
    members: 'contextCopys.amigos.members',
    membersEmoji: '👥',
    addMember: 'contextCopys.amigos.addMember',
    activity: 'contextCopys.amigos.activity',
    activityEmoji: '🎮',
    activities: 'contextCopys.amigos.activities',
    activitiesEmoji: '🎮',
    task: 'contextCopys.amigos.task',
    taskEmoji: '🏆',
    tasks: 'contextCopys.amigos.tasks',
    tasksEmoji: '🏆',
    points: 'contextCopys.amigos.points',
    pointsEmoji: '🌟',
    complete: 'contextCopys.amigos.complete',
    completeEmoji: '✓',
    assign: 'contextCopys.amigos.assign',
    assignEmoji: '📌',
    viewAll: 'contextCopys.amigos.viewAll',
    addTask: 'contextCopys.amigos.addTask',
    addActivity: 'contextCopys.amigos.addActivity',
    myProgress: 'contextCopys.amigos.myProgress',
    groupProgress: 'contextCopys.amigos.groupProgress',
    ranking: 'contextCopys.amigos.ranking',
    rankingEmoji: '🏆',
    todayRanking: 'contextCopys.amigos.todayRanking',
    totalRanking: 'contextCopys.amigos.totalRanking',
    adminRole: 'contextCopys.amigos.adminRole',
    memberRole: 'contextCopys.amigos.memberRole'
  },
  
  // EDUCACIÓN
  grado: {
    dashboard: 'contextCopys.grado.dashboard',
    dashboardEmoji: '🎓',
    createNew: 'contextCopys.grado.createNew',
    member: 'contextCopys.grado.member',
    memberEmoji: '👨‍🎓',
    members: 'contextCopys.grado.members',
    membersEmoji: '👨‍🎓',
    addMember: 'contextCopys.grado.addMember',
    activity: 'contextCopys.grado.activity',
    activityEmoji: '📖',
    activities: 'contextCopys.grado.activities',
    activitiesEmoji: '📖',
    task: 'contextCopys.grado.task',
    taskEmoji: '📝',
    tasks: 'contextCopys.grado.tasks',
    tasksEmoji: '📝',
    points: 'contextCopys.grado.points',
    pointsEmoji: '⭐',
    complete: 'contextCopys.grado.complete',
    completeEmoji: '📤',
    assign: 'contextCopys.grado.assign',
    assignEmoji: '📌',
    viewAll: 'contextCopys.grado.viewAll',
    addTask: 'contextCopys.grado.addTask',
    addActivity: 'contextCopys.grado.addActivity',
    myProgress: 'contextCopys.grado.myProgress',
    groupProgress: 'contextCopys.grado.groupProgress',
    ranking: 'contextCopys.grado.ranking',
    rankingEmoji: '🎓',
    todayRanking: 'contextCopys.grado.todayRanking',
    totalRanking: 'contextCopys.grado.totalRanking',
    adminRole: 'contextCopys.grado.adminRole',
    memberRole: 'contextCopys.grado.memberRole'
  },
  
  curso: {
    dashboard: 'contextCopys.curso.dashboard',
    dashboardEmoji: '📚',
    createNew: 'contextCopys.curso.createNew',
    member: 'contextCopys.curso.member',
    memberEmoji: '🧑‍🎓',
    members: 'contextCopys.curso.members',
    membersEmoji: '🧑‍🎓',
    addMember: 'contextCopys.curso.addMember',
    activity: 'contextCopys.curso.activity',
    activityEmoji: '📘',
    activities: 'contextCopys.curso.activities',
    activitiesEmoji: '📘',
    task: 'contextCopys.curso.task',
    taskEmoji: '✏️',
    tasks: 'contextCopys.curso.tasks',
    tasksEmoji: '✏️',
    points: 'contextCopys.curso.points',
    pointsEmoji: '⭐',
    complete: 'contextCopys.curso.complete',
    completeEmoji: '📤',
    assign: 'contextCopys.curso.assign',
    assignEmoji: '📌',
    viewAll: 'contextCopys.curso.viewAll',
    addTask: 'contextCopys.curso.addTask',
    addActivity: 'contextCopys.curso.addActivity',
    myProgress: 'contextCopys.curso.myProgress',
    groupProgress: 'contextCopys.curso.groupProgress',
    ranking: 'contextCopys.curso.ranking',
    rankingEmoji: '📚',
    todayRanking: 'contextCopys.curso.todayRanking',
    totalRanking: 'contextCopys.curso.totalRanking',
    adminRole: 'contextCopys.curso.adminRole',
    memberRole: 'contextCopys.curso.memberRole'
  },
  
  grupo: {
    dashboard: 'contextCopys.grupo.dashboard',
    dashboardEmoji: '🎯',
    createNew: 'contextCopys.grupo.createNew',
    member: 'contextCopys.grupo.member',
    memberEmoji: '👨‍💼',
    members: 'contextCopys.grupo.members',
    membersEmoji: '👥',
    addMember: 'contextCopys.grupo.addMember',
    activity: 'contextCopys.grupo.activity',
    activityEmoji: '📊',
    activities: 'contextCopys.grupo.activities',
    activitiesEmoji: '📊',
    task: 'contextCopys.grupo.task',
    taskEmoji: '📋',
    tasks: 'contextCopys.grupo.tasks',
    tasksEmoji: '📋',
    points: 'contextCopys.grupo.points',
    pointsEmoji: '⭐',
    complete: 'contextCopys.grupo.complete',
    completeEmoji: '✓',
    assign: 'contextCopys.grupo.assign',
    assignEmoji: '📌',
    viewAll: 'contextCopys.grupo.viewAll',
    addTask: 'contextCopys.grupo.addTask',
    addActivity: 'contextCopys.grupo.addActivity',
    myProgress: 'contextCopys.grupo.myProgress',
    groupProgress: 'contextCopys.grupo.groupProgress',
    ranking: 'contextCopys.grupo.ranking',
    rankingEmoji: '📊',
    todayRanking: 'contextCopys.grupo.todayRanking',
    totalRanking: 'contextCopys.grupo.totalRanking',
    adminRole: 'contextCopys.grupo.adminRole',
    memberRole: 'contextCopys.grupo.memberRole'
  }
};

/**
 * Obtiene los copys personalizados para un tipo de grupo
 */
export function getContextCopys(groupType: GroupType): ContextCopys {
  return CONTEXT_COPYS[groupType] || CONTEXT_COPYS.familia;
}

// ============================================
// CONFIGURACIÓN DE LICENCIAS
// ============================================

export interface LicenseConfig {
  id: LicenseType;
  name: string;
  icon: string;
  description: string;
  tagline: string;
}

export const LICENSE_CONFIGS: Record<LicenseType, LicenseConfig> = {
  familia: {
    id: 'familia',
    name: 'Logrit Familia',
    icon: '👨‍👩‍👧‍👦',
    description: 'Organiza las actividades de tu familia, pareja o grupo de amigos',
    tagline: 'Fortalece los vínculos familiares'
  },
  educacion: {
    id: 'educacion',
    name: 'Logrit Educa',
    icon: '🎓',
    description: 'Gestiona actividades académicas para grados, cursos y grupos de estudio',
    tagline: 'Educación organizada y efectiva'
  }
};

/**
 * Obtiene la configuración de una licencia
 */
export function getLicenseConfig(licenseType: LicenseType): LicenseConfig {
  return LICENSE_CONFIGS[licenseType];
}
